import axios from 'axios';
import { getAuthToken, forceLogout } from '@/store/authStore';

const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

const client = axios.create({
  baseURL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

client.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    /*
     * Admin (WeManage) endpoints authenticate with their own X-Admin-Token, not
     * the app session. A 401 there means "re-enter your authenticator code" and
     * is handled locally by the admin page — it must NOT log the user out of
     * the whole app or redirect to /login.
     *
     * Matched as a PATH SEGMENT, not a substring. `url.includes('/admin')` also
     * matched things like /businesses/admin-consulting and /posts?q=/admin, and
     * the consequence ran the wrong way: a genuine session expiry on one of
     * those requests would be swallowed, leaving the user in a signed-out app
     * that silently fails every action instead of sending them to /login.
     */
    const path = (error?.config?.url || '').split('?')[0];
    const isAdminRequest = /^\/?admin(\/|$)/.test(path);
    // Signed up but no card yet (card-required signup) — the server answers
    // every app API with 402 payment_required until the payment page is done.
    // Covers a stale cached user that slipped past ProtectedRoute's redirect.
    if (
      status === 402 &&
      error?.response?.data?.code === 'payment_required' &&
      typeof window !== 'undefined' &&
      !window.location.pathname.startsWith('/subscribe') &&
      // The admin console is never sent to the plan popup.
      !window.location.pathname.startsWith('/adminwemanage')
    ) {
      window.location.assign('/subscribe');
    }
    if (status === 401 && !isAdminRequest) {
      forceLogout();
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.assign('/login');
      }
    }
    return Promise.reject(error);
  }
);

// Helper to normalize API error messages
export function apiError(error, fallback = 'Something went wrong') {
  const data = error?.response?.data;
  if (data?.message) return data.message;
  if (data?.errors) {
    const first = Object.values(data.errors)[0];
    if (Array.isArray(first)) return first[0];
  }
  return error?.message || fallback;
}

export default client;
